/**
 * Money is stored in minor units (cents) as an integer, never as a float.
 * Formatting is delegated to Intl so locale and currency rules stay correct.
 */
export type CurrencyCode = 'USD' | 'EUR' | 'MXN' | 'BRL' | 'COP' | 'BOB' | 'GBP' | 'USDC'

export interface Money {
  readonly minorUnits: number
  readonly currency: CurrencyCode
}

/** Currencies that do not use 2 decimal places, plus our non-ISO tokens. */
const MINOR_UNIT_EXPONENT: Partial<Record<CurrencyCode, number>> = { COP: 2, USDC: 2 }
const NON_ISO: Partial<Record<CurrencyCode, string>> = { USDC: 'USDC' }

const exponentOf = (currency: CurrencyCode): number => MINOR_UNIT_EXPONENT[currency] ?? 2

export const money = (minorUnits: number, currency: CurrencyCode): Money => ({
  minorUnits: Math.round(minorUnits),
  currency,
})

/** Builds Money from a major-unit amount, e.g. fromMajor(41208.93, 'USD'). */
export const fromMajor = (amount: number, currency: CurrencyCode): Money =>
  money(Math.round(amount * 10 ** exponentOf(currency)), currency)

export const toMajor = (value: Money): number => value.minorUnits / 10 ** exponentOf(value.currency)

export const isNegative = (value: Money): boolean => value.minorUnits < 0

export interface FormatMoneyOptions {
  /** Drop the decimal part when the amount is whole. Default false. */
  readonly compactDecimals?: boolean | undefined
  /** Always show a leading + or −. Default false. */
  readonly signed?: boolean | undefined
  readonly locale?: string | undefined
}

export function formatMoney(value: Money, options: FormatMoneyOptions = {}): string {
  const { compactDecimals = false, signed = false, locale = 'en-US' } = options
  const exponent = exponentOf(value.currency)
  const major = toMajor(value)
  const whole = value.minorUnits % 10 ** exponent === 0
  const fractionDigits = compactDecimals && whole ? 0 : exponent

  const nonIso = NON_ISO[value.currency]
  const formatted = nonIso
    ? `${new Intl.NumberFormat(locale, {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(Math.abs(major))} ${nonIso}`
    : new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: value.currency,
        currencyDisplay: 'symbol',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }).format(Math.abs(major))

  if (signed) return `${value.minorUnits < 0 ? '−' : '+'}${formatted}`
  return value.minorUnits < 0 ? `−${formatted}` : formatted
}

export function formatPercent(fraction: number, locale = 'en-US'): string {
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
    signDisplay: 'exceptZero',
  }).format(fraction)
}
